import React, { useEffect } from 'react';
import { GetServerSideProps, Redirect } from 'next';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useFormat } from 'helpers/hooks/useFormat';
import { appendPageSizeForPlpMobile } from 'helpers/utils/appendPageSizeForPlp';
import { singlePdpPage } from 'helpers/utils/pdpNavigation';
import { pdpNotFound } from 'helpers/utils/pdpNotFound';
import {
  createClient,
  ResponseError,
  LocaleStorage,
  useDarkMode,
  PageDataResponse,
  RedirectResponse,
} from 'frontastic';
import { FrontasticRenderer } from 'frontastic/lib/renderer';
import { tastics } from 'frontastic/tastics';
import { Log } from '../helpers/errorLogger';
import styles from './slug.module.css';

type SlugProps = {
  // This needs an overhaul. Can be too many things in my opinion (*Marcel)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // data: RedirectResponse | PageDataResponse | ResponseError | { ok: string; message: string } | string;
  locale: string;
};

export default function Slug({ data, locale }: SlugProps) {
  LocaleStorage.locale = locale;

  const { applyTheme } = useDarkMode();

  useEffect(() => {
    applyTheme(data?.pageFolder?.configuration?.theme);
  }, [data?.pageFolder?.configuration]);

  const { formatMessage } = useFormat({ name: 'common' });

  if (!data || typeof data === 'string') {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  if (!data!.ok && data!.message) {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data!.message}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  return (
    <>
      <FrontasticRenderer data={data} tastics={tastics} wrapperClassName={styles.gridWrapper} />
    </>
  );
}

export const getServerSideProps: GetServerSideProps | Redirect = async ({ params, locale, query, req, res }) => {
  // console.log('\x1b[31mWHAT STARTED IT??: ' + JSON.stringify(query) + '\x1b[0m');
  const url = req.url ?? '';
  const slug = (query.slug ?? []) as string[];

  const isStaticAssetRequest =
    url.startsWith('/_next/static') ||
    url.startsWith('/undefined') ||
    url.startsWith('/favicon.ico') ||
    url.startsWith('/.well-known') ||
    slug.includes('.well-known');

  /* //CONTINGENCY URLS TO LATER USE
    // slug.includes('undefined') ||
    // url.startsWith('/static/') ||
    // url.startsWith('/robots.txt') ||
    // slug.includes('assets') ||
    // /\.(js|css|png|jpg|jpeg|gif|svg|ico|webp|woff2?|ttf|eot|otf)$/.test(url) ||
    */

  if (isStaticAssetRequest) {
    res.statusCode = 404;
    res.end();
    return { props: {} as any }; // Safe fallback
  }

  LocaleStorage.locale = locale;
  const frontastic = createClient();
  const isPLP = query?.slug?.includes('browse');
  if (isPLP) {
    appendPageSizeForPlpMobile({ query, req });
  }
  const data = await frontastic.getRouteData(params, locale, query, req, res);
  if (isPLP) {
    const redirectUrl = (data as PageDataResponse)?.data?.dataSources?.__master?.redirect;
    if (redirectUrl) {
      return {
        redirect: {
          destination: redirectUrl,
          statusCode: (data as RedirectResponse).statusCode,
        } as Redirect,
      };
    }
    const url = singlePdpPage(data)?.singlePdpUrl;
    if (url) {
      return {
        redirect: {
          destination: url,
          statusCode: (data as RedirectResponse).statusCode,
        } as Redirect,
      };
    }
  }
  if (typeof query.path === 'string' && /\/([^\/]+)\/(DSCO\d{10}|\d{7})/.test(query.path)) {
    const pdpNotFoundPage = pdpNotFound((data as PageDataResponse)?.data?.dataSources?.__master, query.path);
    if (pdpNotFoundPage) {
      return {
        redirect: {
          destination: '/not-found',
          statusCode: (data as RedirectResponse).statusCode,
        } as Redirect,
      };
    }
  }

  if (data) {
    if (data instanceof ResponseError && data.getStatus() == 404) {
      return {
        redirect: {
          destination: '/not-found',
          statusCode: (data as RedirectResponse).statusCode,
        } as Redirect,
      };
    } else if (typeof data === 'object' && 'target' in data) {
      return {
        redirect: {
          destination: data.target,
          statusCode: data.statusCode,
        } as Redirect,
      };
    }
  }

  if (data instanceof Error) {
    // @TODO: Render nicer error page in debug mode, which shows the error to
    // the developer and also outlines how to debug this (take a look at
    // frontastic-CLI).
    Log.error('Error retrieving data: ', data);
    return {
      notFound: true,
    };
  }

  if (typeof data === 'string') {
    return {
      props: {
        data: { error: data },
        error: data,
      },
    };
  }

  if ((data as any)!.message === 'Could not resolve page from path') {
    return {
      redirect: {
        destination: '/not-found',
        statusCode: (data as RedirectResponse).statusCode,
      } as Redirect,
    };
  }

  return {
    props: {
      data:
        data != null
          ? {
              userAgent: req.headers?.['useragent'] != null ? req.headers['useragent'] : req.headers['user-agent'],
              ...data,
            }
          : null,
      locale: locale,
      ...(await serverSideTranslations(locale, [
        'common',
        'cart',
        'product',
        'checkout',
        'account',
        'error',
        'success',
        'wishlist',
        'newsletter',
      ])),
    },
  };
};
